/**
 * QRScanner Component
 * 
 * A reusable QR code scanner component using html5-qrcode library.
 * Supports camera scanning and can extract UUIDs from ClutterLogix QR codes.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopIcon from '@mui/icons-material/Stop';
import { colors, shadows } from '../styles/styleConstants';

/**
 * Valid ClutterLogix domains for QR code validation
 */
const VALID_DOMAINS = [
  'clutterlogix.com',
  'www.clutterlogix.com',
  'localhost:3000',      // Development
  '127.0.0.1:3000',      // Development
];

/**
 * Check if a URL is from a valid ClutterLogix domain
 */
const isValidClutterLogixUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const host = urlObj.host.toLowerCase();
    return VALID_DOMAINS.some(domain => host === domain || host.endsWith('.' + domain));
  } catch {
    return false;
  }
};

/**
 * Extract UUID from a ClutterLogix QR code URL
 * Expected format: https://clutterlogix.com/app/box/uuid/{uuid}
 * Only accepts URLs from valid ClutterLogix domains
 */
const extractUuidFromUrl = (url) => {
  // First validate it's a ClutterLogix URL
  if (!isValidClutterLogixUrl(url)) {
    return null;
  }
  
  // Match UUID pattern in the URL path
  const uuidPattern = /\/box\/uuid\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
  const match = url.match(uuidPattern);
  return match ? match[1] : null;
};

const QRScanner = ({ onScan, onError, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const startScanner = async () => {
    setError(null);
    
    try {
      // Check for camera permission
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);
      
      // Initialize scanner
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" }, // Prefer back camera
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          // Successfully scanned
          handleScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Scan error (usually just "no QR code found" - ignore these)
        }
      );
      
      setIsScanning(true);
    } catch (err) {
      console.error('Scanner error:', err);
      if (err.name === 'NotAllowedError') {
        setHasPermission(false);
        setError('Camera permission denied. Please allow camera access to scan QR codes.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError(`Failed to start scanner: ${err.message}`);
      }
      if (onError) onError(err);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setIsScanning(false);
  };

  const handleScanSuccess = async (decodedText) => {
    // Stop scanning immediately
    await stopScanner();
    
    // Extract UUID from the scanned content - must be a valid ClutterLogix URL
    const uuid = extractUuidFromUrl(decodedText);
    
    if (uuid) {
      if (onScan) onScan({ uuid, rawContent: decodedText });
    } else {
      // Provide helpful error message based on what was scanned
      let errorMsg = 'This QR code is not a valid ClutterLogix box code.';
      
      try {
        const urlObj = new URL(decodedText);
        // It's a URL but not from ClutterLogix
        errorMsg = `This QR code links to "${urlObj.host}" which is not a ClutterLogix domain.`;
      } catch {
        // Not a URL at all
        if (decodedText.length > 50) {
          errorMsg = 'This QR code does not contain a valid ClutterLogix URL.';
        } else {
          errorMsg = `This QR code contains "${decodedText}" which is not a ClutterLogix URL.`;
        }
      }
      
      setError(errorMsg);
      // Restart scanner after showing error
      setTimeout(() => {
        setError(null);
        startScanner();
      }, 3000);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        maxWidth: 400,
        mx: 'auto',
        textAlign: 'center'
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: colors.darkSlateBlue }}>
        Scan QR Code
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {hasPermission === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Camera access is required to scan QR codes. Please enable camera permissions in your browser settings.
        </Alert>
      )}
      
      {/* Scanner viewport */}
      <Box
        id="qr-reader"
        ref={scannerRef}
        sx={{
          width: '100%',
          minHeight: isScanning ? 300 : 0,
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          '& video': {
            borderRadius: 2
          }
        }}
      />
      
      {!isScanning ? (
        <Button
          variant="contained"
          startIcon={<CameraAltIcon />}
          onClick={startScanner}
          fullWidth
          sx={{
            backgroundColor: colors.actionBlue,
            py: 1.5,
            '&:hover': {
              backgroundColor: colors.actionBlue,
              transform: 'translateY(-2px)',
              boxShadow: shadows.md
            }
          }}
        >
          Start Scanner
        </Button>
      ) : (
        <Button
          variant="outlined"
          startIcon={<StopIcon />}
          onClick={stopScanner}
          fullWidth
          color="error"
          sx={{ py: 1.5 }}
        >
          Stop Scanner
        </Button>
      )}
      
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Point your camera at a ClutterLogix QR code label
      </Typography>
    </Paper>
  );
};

export default QRScanner;
